import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, spacing } from 'ui/theme';

import { AncestorIdData, AncestorNameData, FolderId } from 'modules/domains/foldering';
import { isFolderValidDestination as isFolderValid } from './utils';
import { TreeFolder } from './TreeFolder';
import { TreeViewFolderData, TreeViewQueryResponse } from './types';
import { CircleSpinner } from 'ui/spinner';

export type TreeViewProps = {
  /** Default anestors to show, if not provided will use root folder ancestors */
  defaultSelectedAncestors: (AncestorIdData & AncestorNameData)[];
  /** Default name of folder to have selected */
  defaultSelectedFolderName: string;
  /** Default folder to have selected. The TreeView will render in a location so this can be provided */
  defaultSelectedFolder: FolderId;
  /** the currently selected folder ids that are going to be moved */
  folderIdsToMove?: FolderId[];
  /** Function run when folder hierarchy is changed */
  handleChange: (folderData: TreeViewFolderData) => void;
  /** Query uesd to fetch folder data, content, and ancestors for tree folders */
  query: (folderId: FolderId) => TreeViewQueryResponse;
  /** Search text for folder names */
  searchText: string;
  /** Root folder id */
  rootFolderId: number;
  /** Whether the tree view is loading */
  isLoading?: boolean;
};

const StyledContainer = styled.div`
  background: ${colors.white};
  border: solid ${spacing.single} ${colors.grey15};
  border-radius: ${spacing.quarter};
  height: 45vh;
  overflow: auto;
`;

export const TreeView = ({
  defaultSelectedAncestors,
  defaultSelectedFolderName,
  defaultSelectedFolder,
  folderIdsToMove = [],
  handleChange,
  query,
  rootFolderId,
  searchText = '',
  isLoading = false,
}: TreeViewProps) => {
  const [selectedFolder, setSelectedFolder] = useState<FolderId>(defaultSelectedFolder);
  const [selectedFolderName, setSelectedFolderName] = useState<string>(defaultSelectedFolderName);
  const [selectedAncestors, setSelectedAncestors] =
    useState<(AncestorIdData & AncestorNameData)[]>(defaultSelectedAncestors);

  useEffect(() => {
    handleChange({ id: selectedFolder, name: selectedFolderName, ancestors: selectedAncestors });
  }, [selectedFolder, selectedFolderName, selectedAncestors]);

  const handleFolderSelection = (
    folderId: FolderId,
    folderName: string,
    ancestors?: (AncestorIdData & AncestorNameData)[],
  ) => {
    setSelectedFolder(folderId);
    setSelectedFolderName(folderName);
    setSelectedAncestors(ancestors || []);
  };

  const isFolderValidDestination = (folderId: FolderId) => isFolderValid(folderId, folderIdsToMove);

  return (
    <StyledContainer data-test="tree-view-container" role="tree">
      {isLoading ? (
        <CircleSpinner />
      ) : (
        <TreeFolder
          ancestors={[]}
          checkIfValid={isFolderValidDestination}
          id={rootFolderId}
          indentLevel={0}
          name=""
          query={query}
          rootFolderId={rootFolderId}
          searchText={searchText}
          selectedAncestors={selectedAncestors}
          selectedFolderId={selectedFolder}
          setSelectedFolder={handleFolderSelection}
        />
      )}
    </StyledContainer>
  );
};
